import { revertAll } from "@/app/model/actions";
import { IApiError, PageSizeType } from "@/app/model/types";
import {
  EditUserFieldsType,
  IUser,
  UserSliceState,
  UsersBodyType,
  UsersResponse,
  BlockHistoryResponse,
  ChangeManagerDealersResponse,
} from "@/entities/users/types";
import $api from "@/shared/http";
import { toApiError } from "@/shared/http/types";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";

const initialState: UserSliceState = {
  usersList: [],
  usersListForFilter: [],
  error: null,
  sortRules: {
    type: "desc",
    field: "fio",
  },
  hideUserColumnsList: [],
  isLoading: false,
  miniLoading: false,
  userForReview: null,
  searchCode: null,
  pageSize: (+localStorage.getItem("psizeN") as PageSizeType) || 20,
  blockHistories: [],
};

//Добавить пользователя
export const addUser = createAsyncThunk<
  IUser,
  Partial<IUser>,
  { rejectValue: IApiError }
>("users/addUser", async (body: Partial<IUser>, { rejectWithValue }) => {
  try {
    const response = await $api.post("/user/register", body);
    return response.data;
  } catch (error) {
    const axiosError = error as {
      response?: {
        data: IApiError;
        status: number;
      };
      message: string;
    };

    if (error.response?.status === 500) {
      return rejectWithValue({
        title: "Ошибка",
        status: 500,
        detail: "Ошибка при создании дилера, Обратитесь в службу поддержки",
      });
    }

    return rejectWithValue(
      axiosError.response?.data || {
        title: "Ошибка",
        status: axiosError.response?.status || 500,
        detail: axiosError.message || "Неизвестная ошибка",
      },
    );
  }
});

//Изменить пользователя
export const editUser = createAsyncThunk(
  "users/editUser",
  async (body: Partial<EditUserFieldsType>, { rejectWithValue }) => {
    try {
      const response = await $api.put("/user", JSON.stringify(body));
      if (response.status !== 200) {
        throw new Error("Ошибка при создании пользователя");
      }
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const fetchBlockHistory = createAsyncThunk<
  BlockHistoryResponse,
  string,
  { rejectValue: string }
>("users/fetchBlockHistory", async (userId, { rejectWithValue }) => {
  try {
    const response = await $api.get<BlockHistoryResponse>(
      `/user/blockhistory?userId=${userId}`,
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

//Получить пользователя
export const fetchUsers = createAsyncThunk<
  UsersResponse,
  "" | UsersBodyType,
  { rejectValue: string }
>("users/fetchUsers", async (value = "", { rejectWithValue }) => {
  let body = {
    order: {
      type: "asc",
      field: "role",
    },
    pagination: {
      pageSize: 999,
      pageNumber: 1,
    },
    filters: [] as object[],
  };
  if (value) {
    const { filters, pagination } = value;
    body = {
      order: {
        type: "asc",
        field: "role",
      },
      pagination,
      filters,
    };
  }
  try {
    const response = await $api.post<UsersResponse>(
      "/user",
      JSON.stringify(body),
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const recoveryPassword = createAsyncThunk<
  void,
  { email: string },
  { rejectValue: string }
>("user/recoveryPassword", async (body, { rejectWithValue }) => {
  try {
    await $api.post("/user/password/forgot", body);
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const resetPassword = createAsyncThunk<
  void,
  {
    email: string;
    resetCode: string;
    newPassword: string;
  },
  { rejectValue: string }
>("user/resetPassword", async (body, { rejectWithValue }) => {
  try {
    await $api.post("/user/password/reset", body);
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const blockUser = createAsyncThunk<
  void,
  { id: string; comment: string },
  { rejectValue: string }
>("user/blockUser", async (body, { rejectWithValue }) => {
  try {
    await $api.post("/user/block", body);
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const unblockUser = createAsyncThunk<
  void,
  { id: string; comment: string },
  { rejectValue: string }
>("user/unblockUser", async (body, { rejectWithValue }) => {
  try {
    await $api.post("/user/unblock", body);
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

//Изменить список привязанных дилеров пользователя
export const changeManagerDealers = createAsyncThunk<
  ChangeManagerDealersResponse,
  {
    userId: string;
    dealerIds: string[];
  },
  { rejectValue: string }
>("user/changeManagerDealers", async (body, { rejectWithValue }) => {
  try {
    const response = await $api.post<ChangeManagerDealersResponse>(
      "/user/link/dealer",
      body,
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    sortUserList: (state, action) => {
      const nameSort: keyof IUser | null = action.payload.field;
      const typeSort = action.payload.type;
      if (nameSort && typeSort !== null && state.usersList.length > 1) {
        state.usersList = state.usersList.sort((user1, user2): number => {
          if (nameSort === "managerDealers") return 0;
          if (!user1[nameSort] || !user2[nameSort]) return 0;
          if (typeSort === "asc") {
            return user1[nameSort].toLowerCase()! >
              user2[nameSort].toLowerCase()!
              ? 1
              : -1;
          } else if (typeSort === "desc") {
            return user1[nameSort].toLowerCase()! <
              user2[nameSort].toLowerCase()!
              ? 1
              : -1;
          } else {
            return 0;
          }
        });
      }
    },

    searchUser: (state, action) => {
      if (action.payload) {
        state.usersList = state.usersListForFilter.filter((user) => {
          if (!user.fio) return true;
          if (
            user.fio &&
            user.fio.toLowerCase().indexOf(action.payload.toLowerCase()) >= 0
          ) {
            return true;
          } else {
            return false;
          }
        });
      } else {
        state.usersList = state.usersListForFilter;
      }
    },
    setUsersColumnsToHide(state, action) {
      state.hideUserColumnsList = action.payload;
    },
    setSortRules(state, action: PayloadAction<typeof state.sortRules>) {
      state.sortRules = action.payload;
    },
    setSearchCode(state, action: PayloadAction<typeof state.searchCode>) {
      state.searchCode = action.payload;
    },
    setPageSize(state, action: PayloadAction<typeof state.pageSize>) {
      state.pageSize = action.payload;
    },
    showUserDetails(state, action: PayloadAction<string>) {
      const user = state.usersList.find((user) => user.id === action.payload);
      state.userForReview = user;
    },
    setUserForReview(state, action: PayloadAction<IUser>) {
      state.userForReview = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(revertAll, () => initialState);

    builder.addCase(fetchUsers.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });

    builder.addCase(
      fetchUsers.fulfilled,
      (state, action: PayloadAction<UsersResponse>) => {
        state.usersList = action.payload.items;
        state.usersListForFilter = action.payload.items;
        state.isLoading = false;
        state.error = null;
      },
    );

    builder.addCase(fetchUsers.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });
    builder.addCase(addUser.pending, (state) => {
      state.error = null;
      state.miniLoading = true;
    });

    builder.addCase(addUser.fulfilled, (state) => {
      state.error = null;
      state.miniLoading = false;
    });

    builder.addCase(addUser.rejected, (state, action) => {
      state.error = action.payload;
      state.miniLoading = false;
    });
    builder.addCase(editUser.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });

    builder.addCase(editUser.fulfilled, (state) => {
      state.error = null;
    });

    builder.addCase(editUser.rejected, (state, action) => {
      state.error = action.payload;
    });

    builder.addCase(recoveryPassword.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });

    builder.addCase(recoveryPassword.fulfilled, (state) => {
      state.error = null;
      state.isLoading = false;
    });

    builder.addCase(recoveryPassword.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });

    builder.addCase(resetPassword.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });

    builder.addCase(resetPassword.fulfilled, (state) => {
      state.error = null;
      state.isLoading = false;
    });

    builder.addCase(resetPassword.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });
    builder.addCase(blockUser.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });

    builder.addCase(blockUser.fulfilled, (state) => {
      state.error = null;
    });

    builder.addCase(blockUser.rejected, (state, action) => {
      state.error = action.payload;
    });
    builder.addCase(unblockUser.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });

    builder.addCase(unblockUser.fulfilled, (state) => {
      state.error = null;
    });

    builder.addCase(unblockUser.rejected, (state, action) => {
      state.error = action.payload;
    });
    builder.addCase(fetchBlockHistory.pending, (state) => {
      state.error = null;
    });

    builder.addCase(fetchBlockHistory.fulfilled, (state, action) => {
      state.error = null;
      state.blockHistories = action.payload?.data;
    });

    builder.addCase(fetchBlockHistory.rejected, (state, action) => {
      state.error = action.payload;
    });
    builder.addCase(changeManagerDealers.pending, (state) => {
      state.error = null;
    });

    builder.addCase(changeManagerDealers.fulfilled, (state, action) => {
      state.error = null;
      if (action.payload.user) {
        state.userForReview = action.payload.user;
      }
    });

    builder.addCase(changeManagerDealers.rejected, (state, action) => {
      state.error = action.payload;
    });
  },
});

export const {
  setPageSize,
  setSearchCode,
  setSortRules,
  setUsersColumnsToHide,
  showUserDetails,
  sortUserList,
  searchUser,
  setUserForReview,
} = usersSlice.actions;
